<!-- src/apps/colonel/views/ColonelSecrets.vue -->

<script setup lang="ts">
  import ColonelListPage from '@/apps/colonel/components/ColonelListPage.vue';
  import ColonelPagination from '@/apps/colonel/components/ColonelPagination.vue';
  import { useColonelInfoStore } from '@/shared/stores/colonelInfoStore';
  import { formatDisplayDateTime } from '@/utils/format';
  import { storeToRefs } from 'pinia';
  import { computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  const stateLabels = computed(() => ({
    new: t('web.colonel.secrets.state.new'),
    received: t('web.colonel.secrets.state.received'),
    viewed: t('web.colonel.secrets.state.viewed'),
  }));

  const store = useColonelInfoStore();
  const { secrets, secretsPagination, loading, secretsFetchError } = storeToRefs(store);
  const { fetchSecrets } = store;

  onMounted(() => fetchSecrets());
</script>

<template>
  <ColonelListPage
    :loading="loading.secrets"
    :title="t('web.colonel.secrets.title')"
    :description="t('web.colonel.secrets.description')"
    :fetch-error="secretsFetchError"
    resource="secrets">
    <template
      v-if="secretsPagination"
      #count>
      Showing {{ secrets.length }} of {{ secretsPagination.total_count }} secrets
    </template>

    <template #default>
      <div
        v-if="secrets.length > 0"
        class="overflow-x-auto">
        <table
          data-testid="colonel-secrets-table"
          class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                {{ t('web.colonel.secrets.columns.shortId') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                {{ t('web.colonel.secrets.columns.state') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                {{ t('web.colonel.secrets.columns.created') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                {{ t('web.colonel.secrets.columns.expiration') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                {{ t('web.colonel.secrets.columns.age') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
            <tr
              v-for="secret in secrets"
              :key="secret.secret_id"
              class="hover:bg-gray-50 dark:hover:bg-gray-800">
              <td
                class="whitespace-nowrap px-6 py-4 font-mono text-sm text-gray-900 dark:text-white">
                {{ secret.shortid }}
              </td>
              <td class="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                <span
                  :class="{
                    'rounded bg-green-100 px-2 py-1 text-green-800 dark:bg-green-900 dark:text-green-200':
                      secret.state === 'new',
                    'rounded bg-yellow-100 px-2 py-1 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200':
                      secret.state === 'received',
                    'rounded bg-gray-100 px-2 py-1 text-gray-800 dark:bg-gray-900 dark:text-gray-200':
                      secret.state === 'viewed',
                  }">
                  {{ stateLabels[secret.state as keyof typeof stateLabels] || secret.state }}
                </span>
              </td>
              <td class="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                {{ formatDisplayDateTime(secret.created) }}
              </td>
              <td class="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                {{ secret.expiration ? formatDisplayDateTime(secret.expiration) : t('web.colonel.secrets.never') }}
              </td>
              <td class="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                {{ Math.floor(secret.age / 86400) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-else
        class="py-12 text-center text-gray-500 dark:text-gray-400">
        {{ t('web.colonel.secrets.empty') }}
      </div>

      <ColonelPagination
        v-if="secretsPagination"
        class="mt-4"
        :pagination="secretsPagination"
        :loading="loading.secrets"
        @update:page="(page) => fetchSecrets(page, secretsPagination?.per_page ?? 50)"
        @update:per-page="(perPage) => fetchSecrets(1, perPage)" />
    </template>
  </ColonelListPage>
</template>
